import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface ExpenseReport {
  id: string;
  user_id: string;
  title: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  currency: string;
  total_cents: string;
  submitted_at: string | null;
  created_at: string;
}

export interface ExpenseLine {
  id: string;
  report_id: string;
  occurred_on: string;
  description: string;
  category: string;
  amount_cents: string;
  currency: string;
  tax_rate: string | null;
  jp_qualified: boolean;
  receipt_url: string | null;
  created_at: string;
}

export function useExpenseReports(wsId: string | undefined, mine = false) {
  return useQuery<ExpenseReport[]>({
    queryKey: ['expense-reports', wsId, mine],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get<ExpenseReport[]>(`/workspaces/${wsId}/expenses${mine ? '?mine=1' : ''}`)).data,
  });
}

export function useExpenseReport(wsId: string | undefined, id: string | null | undefined) {
  return useQuery<{ report: ExpenseReport; lines: ExpenseLine[] }>({
    queryKey: ['expense-report', wsId, id],
    enabled: !!wsId && !!id,
    queryFn: async () => (await api.get(`/workspaces/${wsId}/expenses/${id}`)).data,
  });
}

export function useCreateExpenseReport(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title: string; currency: string }) =>
      (await api.post<ExpenseReport>(`/workspaces/${wsId}/expenses`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expense-reports', wsId] }),
  });
}

export function useAddExpenseLine(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reportId,
      occurredOn,
      description,
      category,
      amountCents,
      currency,
      taxRate,
      jpQualified,
      receipt,
    }: {
      reportId: string;
      occurredOn: string;
      description: string;
      category: string;
      amountCents: number;
      currency: string;
      taxRate?: number;
      jpQualified?: boolean;
      receipt?: File;
    }) => {
      const form = new FormData();
      form.append('occurredOn', occurredOn);
      form.append('description', description);
      form.append('category', category);
      form.append('amountCents', String(amountCents));
      form.append('currency', currency);
      if (taxRate !== undefined) form.append('taxRate', String(taxRate));
      if (jpQualified !== undefined) form.append('jpQualified', String(jpQualified));
      if (receipt) form.append('receipt', receipt);
      return (
        await api.post(`/workspaces/${wsId}/expenses/${reportId}/lines`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      ).data;
    },
    onSuccess: (_, { reportId }) => {
      qc.invalidateQueries({ queryKey: ['expense-report', wsId, reportId] });
      qc.invalidateQueries({ queryKey: ['expense-reports', wsId] });
    },
  });
}

export function useSubmitExpenseReport(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<ExpenseReport>(`/workspaces/${wsId}/expenses/${id}/submit`)).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['expense-report', wsId, id] });
      qc.invalidateQueries({ queryKey: ['expense-reports', wsId] });
    },
  });
}

export interface PerDiemRates {
  lodgingCapCents: number;
  mealsPerDiemCents: number;
  transportAllowanceCents: number;
  currency: string;
  source: 'workspace' | 'default';
}

export const PER_DIEM_REGIONS = ['jp_domestic', 'jp_metro', 'asia', 'europe', 'americas', 'other'] as const;
export const PER_DIEM_ROLES = ['staff', 'manager', 'executive'] as const;

/**
 * 旅費規程 per-diem rate lookup for a region + role grade. Drives the
 * "pre-fill allowance" affordance in the expense line editor when a
 * travel/per-diem category is chosen. Only fetches when both args are set.
 */
export function usePerDiemRates(
  wsId: string | undefined,
  region: string | undefined,
  role: string | undefined,
) {
  return useQuery<PerDiemRates>({
    queryKey: ['per-diem-rates', wsId, region, role],
    enabled: !!wsId && !!region && !!role,
    queryFn: async () =>
      (await api.get<PerDiemRates>(
        `/workspaces/${wsId}/expenses/per-diem/rates?region=${region}&role=${role}`,
      )).data,
  });
}

/** Approve / reject / mark-paid a submitted expense report. */
export function useExpenseStatus(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'approved' | 'rejected' | 'paid' | 'draft' }) =>
      (await api.post<ExpenseReport>(`/workspaces/${wsId}/expenses/${id}/status/${status}`)).data,
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['expense-report', wsId, id] });
      qc.invalidateQueries({ queryKey: ['expense-reports', wsId] });
    },
  });
}
